import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Is Missionary Support Tax-Deductible in Australia? DGR Status Explained (2026)';
const DESCRIPTION =
    "A plain-English guide for Australian donors: why Deductible Gift Recipient (DGR) status decides whether a gift to a missionary is tax-deductible, and how missionary giving is usually structured to qualify.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover18.jpg',
            datePublished: '2026-08-14',
            dateModified: '2026-08-14',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/is-missionary-support-tax-deductible-australia',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Is a gift straight to a missionary tax-deductible in Australia?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "No. Only gifts to an organisation endorsed by the ATO as a Deductible Gift Recipient (DGR) are tax-deductible. A personal gift to an individual missionary, no matter how legitimate the work, is not deductible because the recipient isn't a DGR-endorsed entity.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How does DGR status work?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "An organisation applies to the ATO for Deductible Gift Recipient endorsement under a specific category. Once endorsed, gifts of $2 or more to that organisation are deductible on the donor's individual tax return, reducing taxable income at the donor's marginal rate.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How do Australian missionaries offer deductible giving?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Through a mission agency or denomination that holds DGR endorsement itself, or via a fund it operates that qualifies. The donor gives to the DGR-endorsed entity, which must retain discretion over the funds even when most of the gift is directed to a specific missionary's field account.",
                    },
                },
            ],
        },
    ],
};

export default function IsMissionarySupportTaxDeductibleAustralia() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/is-missionary-support-tax-deductible-australia' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-tax-deductible-australia-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover18.jpg" alt="An Australian supporter checking an organisation's DGR status" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>"Is this a DGR?" is the question that actually decides whether an Australian supporter's gift to a missionary is tax-deductible — not the sincerity of the missionary or the value of the work.</p>

                    <h2>The short answer</h2>
                    <p>A gift is deductible in Australia only when it's made to an organisation endorsed by the ATO as a Deductible Gift Recipient (DGR) — not when it's given directly to an individual, however worthy the ministry. That single requirement explains almost every confusing edge case in missionary giving here.</p>

                    <h2>Why a direct gift to a missionary usually isn't deductible</h2>
                    <p>The ATO's rule is about the identity of the recipient. If a supporter transfers money straight to a missionary's personal bank account to cover living or ministry costs, that's a personal gift — the same as helping out a friend — and it's not deductible, regardless of how the appeal is framed. This applies to most direct, platform-based personal support pages, including GoFundMe-style campaigns and recurring-support platforms like ours that pay a creator directly rather than routing through a DGR-endorsed organisation.</p>

                    <h2>How deductible missionary giving actually works</h2>
                    <p>When an Australian missionary offers tax-deductible giving, there's a DGR-endorsed organisation between the donor and the missionary — usually a denominational mission agency or an independent sending organisation that holds DGR status (often under a specific fund, such as an approved overseas aid fund). The donor's gift goes to that organisation, which must retain genuine discretion over how the funds are used, even if in practice it directs most of the gift toward a specific missionary's field account. If the organisation simply passes a donor's earmarked gift straight through to a named individual with no real discretion, it risks the gift not being treated as deductible.</p>
                    <p>Deductible gifts of $2 or more reduce the donor's taxable income at their marginal tax rate — worth more to a donor on a higher marginal rate than to one on a lower one, unlike a flat-rate credit.</p>

                    <h2>Why this matters less than people assume</h2>
                    <p>Deductibility only changes the outcome for donors who itemise this specific deduction and give at a level where it's worth claiming. For a supporter giving $50/month, the extra structure a DGR-endorsed pathway adds is often a bigger trade-off than the tax saved. For a donor on a higher marginal rate giving several thousand dollars a year, the deduction is meaningful and can influence how much — or whether — they give.</p>

                    <h2>What this means if you're choosing how to give — or built to receive</h2>
                    <p>If your Australian supporter base is mostly modest, regular monthly givers, direct support's simplicity usually outweighs a deduction most of them wouldn't individually notice. If a meaningful share of your list gives at scale and cares about the deduction, routing through a DGR-endorsed mission agency is worth the added approval process and administrative fee.</p>
                    <p>We're upfront that Devotion Fund is a direct-support platform: gifts aren't tax-deductible today, because we're not DGR-endorsed. If deductibility is the deciding factor for your support base, an Australian mission agency with DGR status is the more honest fit.</p>

                    <h2>Frequently asked questions</h2>
                    <p><strong>Is a gift straight to a missionary tax-deductible in Australia?</strong><br />
                    No — only gifts to a DGR-endorsed organisation are deductible.</p>
                    <p><strong>How does DGR status work?</strong><br />
                    The ATO endorses eligible organisations as Deductible Gift Recipients; gifts of $2+ to them are deductible at the donor's marginal tax rate.</p>
                    <p><strong>How do Australian missionaries offer deductible giving?</strong><br />
                    Through a DGR-endorsed mission agency or fund that retains discretion over the funds rather than acting as a pure pass-through to a named individual.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/is-missionary-support-tax-deductible">Is Missionary Support Tax-Deductible? (US guide)</a></li>
                        <li><a href="/resources/is-missionary-support-tax-deductible-canada">Is Missionary Support Tax-Deductible in Canada?</a></li>
                        <li><a href="/resources/how-to-start-monthly-giving-page-without-501c3">How to Start a Monthly Giving Page Without a 501(c)(3)</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We run a direct, recurring-first support platform — which means being straight with Australian supporters about what it doesn't do, including tax deductibility, is part of the job.</p>
                    <p><small><em>Last updated: 2026-08-14. This article is general information, not tax advice — talk to a qualified Australian tax professional about your specific situation.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/is-missionary-support-tax-deductible-australia" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
